import { CheckCircleIcon } from '@heroicons/react/solid'
import Cancel from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import Error from '@mui/icons-material/Error'
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, TextField } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useMemo, useState, useEffect } from 'react'
import { AvailableScreenerComponent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { getCorrectScreeningReasonMessageObject } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import HoldSlotAndSendLInkToPayButton from 'src/clinic/components/ClinicManageBookingsPage/newBookingCalendar/HoldSlotAndSendLInkToPayButton'
import { EventSelectorForBooking } from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/eventSelector/EventSelectorForBooking'
import SelectedEventDataViewer from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/eventSelector/SelectedEventDataViewer'
import NewBookingDialogReasonForNotDoingScreeningForm from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/NewBookingDialogReasonForNotDoingScreeningForm'
import { getBookingMethodByMentorId } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/bookingUtils/bookMethod'
import { getCPDataWithParams } from 'src/clinic/services'
import {
  CustomHTMLSelect,
  CustomInput,
  CustomSelect,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import {
  currency_list,
  displayNamesOfEventTypes,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  getEventTypeObject,
  OFFLINE_PAYMENT_METHODS_LIST,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_ONLINE,
} from 'src/consumer/constants'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import { isNewBookingFormDataValid } from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/createBooking/createBookingPopupUtils'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  isCommonResponseSuccessful,
} from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'

export default function CreateBookingTab({
  selectedPatient,
  refreshData,
  mentorsList,
  appointmentTypesList,
  mentorProfileData,
  selectedSlotData,
  forMentor = false,
  manuallySelectSlotTiming = false,
  closeDialog,
}) {

  const [formData, setFormData] = useState({
    startDate: dayjs(selectedSlotData?.startTime),
    selectedMentor: mentorProfileData?.id,
    selectedAppointmentType: null,
    currency: 'inr',
    feePaymentMode: 'cash',
    feeCollectionNote: '',
  })


  const [isLoading, setIsLoading] = useState(false)
  const [skipBilling, setSkipBilling] = useState(false)
  const [isBookingLoading, setIsBookingLoading] = useState(false)
  const [creditsContext, setCreditsContext] = useState(null)
  const [creditsContextLoading, setCreditsContextLoading] = useState(false)
  const [tryBookingScreening, setTryBookingScreening] = useState(true)
  const [screenerAvailable, setScreenerAvailable] = useState(false)
  const [reasonForNotDoingScreening, setReasonForNotDoingScreening] = useState('')
  const [reasonIdForNotDoingScreening, setReasonIdForNotDoingScreening] = useState('')


  const selectedMentorProfileData =
    mentorProfileData || mentorsList?.find((mentor) => mentor?.id === formData?.selectedMentor)

  const apptTypesToConsider = useMemo(() => {
    //TODO: SR: MOVE THIS TO A METHOD
    return appointmentTypesList
      ?.filter((apType) => {
        if (selectedMentorProfileData?.id === 'un-assigned') {
          return apType?.type !== EVENT_TYPE_SCREENING
        }
        const canBookTherapyHere =
          selectedMentorProfileData?.categories?.includes('therapist') ||
          selectedMentorProfileData?.categories?.includes('"clinical_psychologist"')
        const canBookConsultationHere = selectedMentorProfileData?.categories?.includes('doctor')

        if (canBookTherapyHere && apType?.type === EVENT_TYPE_THERAPY_SESSION) {
          return apType?.type !== EVENT_TYPE_SCREENING
        }
        if (canBookConsultationHere && apType?.type === EVENT_TYPE_CONSULTATION) {
          return apType?.type !== EVENT_TYPE_SCREENING
        }
        return false
      })
      ?.sort((a, b) => {
        if (a.type === EVENT_TYPE_THERAPY_SESSION && b.type === EVENT_TYPE_CONSULTATION) {
          return -1
        }
        if (a.type === EVENT_TYPE_CONSULTATION && b.type === EVENT_TYPE_THERAPY_SESSION) {
          return 1
        }
        return 0
      })
  }, [appointmentTypesList, selectedMentorProfileData])



  const selectedApptTypeObj = appointmentTypesList?.find(
    (apptType) => apptType?.id === formData?.selectedAppointmentType,
  )

  useEffect(() => {
    let active = true

    const loadCreditsContext = async () => {
      if (!selectedPatient?.id || !selectedApptTypeObj?.pricingPlanId) {
        setCreditsContext(null)
        return
      }

      setCreditsContextLoading(true)
      const response = await getCPDataWithParams(
        '/api/secure/meeting/booking-credits-context',
        {
          patientId: selectedPatient.id,
          subscriptionPlanId: selectedApptTypeObj.pricingPlanId,
          eventTypeId: selectedApptTypeObj?.id,
          eventType: selectedApptTypeObj?.type,
          mentorId: selectedMentorProfileData?.id,
          clinicId: selectedPatient?.clinicId,
          clinicBrandId: selectedPatient?.clinicBrandId,
        },
      )

      if (!active) {
        return
      }

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        setCreditsContext(getDataMapFromCommonResponse(response)?.creditsContext || null)
      } else {
        setCreditsContext(null)
      }
      setCreditsContextLoading(false)
    }

    loadCreditsContext()

    return () => {
      active = false
    }
  }, [
    selectedApptTypeObj?.id,
    selectedApptTypeObj?.pricingPlanId,
    selectedApptTypeObj?.type,
    selectedMentorProfileData?.id,
    selectedPatient?.clinicBrandId,
    selectedPatient?.clinicId,
    selectedPatient?.id,
  ])



  const bookButtonPostText = useMemo(() => {
    let postText = getEventTypeObject(selectedApptTypeObj?.type)?.title || ''
    if (selectedApptTypeObj?.meetingSrc === 'aumhum') {
      postText += ' (Online/Video)'
    } else if (selectedApptTypeObj?.meetingSrc === 'offline') {
      postText += ' (In Clinic)'
    }
    return postText
  }, [selectedApptTypeObj])

  function handleChange(e) {
    genericHandleChange(e, setFormData)
  }

  async function basicBooking({
    paymentGateway,
    sendLinkToPatient,
    sendLinkToRelative,
    customPhonesToSendLinkTo,
    bundlePackPurchaseId,
  }) {
    setIsBookingLoading(true)
    let bookSessionFunction = getBookingMethodByMentorId(formData?.selectedMentor)

    let paymentDetails = {
      eventFee: formData?.fee,
      feePaymentMode: formData?.feePaymentMode,
      feePaidTxnId: formData?.feePaidTxnId,
      feeCollectionNote: formData?.feeCollectionNote || '',
    }

    if (skipBilling) {
      paymentDetails = {
        ...paymentDetails,
        eventFee: 0,
        feePaymentMode: 'cash',
      }
    }
    if (paymentDetails?.feePaymentMode === 'cash') {
      paymentDetails = {
        ...paymentDetails,
        feePaidTxnId: '',
      }
    }
    if (paymentGateway === 'credits') {
      paymentDetails = {
        eventFee: '',
        feePaymentMode: '',
        feePaidTxnId: '',
        feeCollectionNote: '',
      }
    }

    try {
      const response = await bookSessionFunction('/api/secure/meeting/book', {
        isUsingClinicAppointmentType: true,
        mentorId: selectedMentorProfileData?.id,
        eventTypeId: selectedApptTypeObj?.id,
        eventType: selectedApptTypeObj?.type,
        subscriptionPlanId: selectedApptTypeObj?.pricingPlanId,
        startTimeInDayJs: formData?.startDate,
        eventMode: formData?.meetingType || selectedApptTypeObj?.type,
        // eventDurationInMins: formData?.duration || selectedApptTypeObj?.duration,
        eventDurationInMins: selectedApptTypeObj?.duration,
        patientId: selectedPatient?.id,
        clinicId: selectedPatient?.clinicId,
        clinicBrandId: selectedPatient?.clinicBrandId,
        paymentGateway: paymentGateway,
        feeCurrency: formData?.currency,
        bookScreeningAutomatically: tryBookingScreening,
        notScreeningReason: getCorrectScreeningReasonMessageObject({
          eventType: selectedApptTypeObj?.type,
          isScreenerAvailable: screenerAvailable,
          isTryingToBookScreening: tryBookingScreening,
          reasonIdForNotDoingScreening: reasonIdForNotDoingScreening,
          reasonForNotDoingScreening: reasonForNotDoingScreening,
        }),
        sendLinkToPatient,
        sendLinkToRelative,
        customPhonesToSendLinkTo,
        bundlePackPurchaseId,
        ...paymentDetails,
      })
      if (!!response) {
        refreshData()
        closeDialog()
      } else {
        throw new Error('Error Booking Session')
      }
    } catch (error) {
      console.log(error)
    }
    setIsBookingLoading(false)
  }

  async function handleOfflineBooking() {
    await basicBooking({ paymentGateway: 'paidOffline' })
  }

  async function handleCreditsBooking() {
    await basicBooking({
      paymentGateway: 'credits',
      bundlePackPurchaseId: creditsContext?.recommendedBundlePackPurchaseId || '',
    })
  }

  async function handleHoldSlotAndBook({
    sendLinkToPatient = false,
    sendLinkToRelative = false,
    customPhonesToSendLinkTo = [],
  }) {
    await basicBooking({
      paymentGateway: 'paymentLinkInitiatedByMentor',
      sendLinkToPatient,
      sendLinkToRelative,
      customPhonesToSendLinkTo,
    })
  }

  console.log('formData', formData)

  if (!selectedPatient) {
    return <></>
  }

  return (
    <div className="d-flex flex-column gap-2">
      <Grid spacing={2} sx={{ pt: 3 }} container>
        {' '}
        <Grid xs={12} sx={{ p: 1 }}>
          {/* Session Starts - Above Event Type Selection */}
          <Box
            component="div"
            sx={{
              border: '4px solid #6779e3 !important',
              borderRadius: '12px !important',
              padding: '20px !important',
              marginBottom: '24px !important',
              marginTop: '0px !important',
              backgroundColor: '#f0f4ff !important',
              boxShadow: '0 6px 16px rgba(103, 121, 227, 0.3) !important',
              width: '100% !important',
              maxWidth: '100% !important',
              boxSizing: 'border-box !important',
              display: 'flex !important',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <strong style={{ color: '#6779e3', fontSize: '16px', fontWeight: 700, minWidth: '120px' }}>Session Starts:</strong> 
            <span style={{ color: '#1e293b', fontSize: '16px', fontWeight: 600, flex: '1 1 auto' }}>
              {(() => {
                if (!formData?.startDate) return 'Not set'
                const timeStr = formData.startDate.format('DD/MM/YYYY, h:mma')
                // Get timezone abbreviation - default to IST
                let tzAbbr = 'IST'
                try {
                  // Try to get timezone from selectedPatient's clinic data if available
                  const timezone = selectedPatient?.clinicLocationData?.schedules?.[0]?.timezone || 
                                   selectedPatient?.clinicLocationData?.timeZone?.offset || 
                                   '+05:30'
                  const decodedTz = typeof timezone === 'string' && timezone.includes('%') ? decodeURIComponent(timezone) : timezone
                  if (decodedTz === '+05:30' || decodedTz === 'Asia/Kolkata' || decodedTz === 'Asia/Calcutta') {
                    tzAbbr = 'IST'
                  } else if (decodedTz === '+05:00') {
                    tzAbbr = 'PKT'
                  } else if (decodedTz === '+08:00') {
                    tzAbbr = 'CST'
                  } else if (decodedTz === '+00:00' || decodedTz === 'UTC') {
                    tzAbbr = 'UTC'
                  } else {
                    const offsetMatch = decodedTz.match(/^([+-])(\d{2}):(\d{2})$/)
                    if (offsetMatch) {
                      const sign = offsetMatch[1]
                      const hours = parseInt(offsetMatch[2], 10)
                      const minutes = parseInt(offsetMatch[3], 10)
                      tzAbbr = minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${minutes}`
                    } else {
                      tzAbbr = decodedTz
                    }
                  }
                } catch {
                  tzAbbr = 'IST'
                }
                return `${timeStr} (${tzAbbr})`
              })()}
            </span>
            <IconButton
              size="large"
              onClick={() => {
                // TODO: Implement edit session start functionality
                console.log('Edit session start clicked')
              }}
              sx={{
                padding: '10px',
                color: '#6779e3',
                backgroundColor: '#ffffff',
                border: '2px solid #6779e3',
                minWidth: '44px',
                minHeight: '44px',
                '&:hover': {
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '2px solid #7450aa',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <EditIcon sx={{ fontSize: '24px', display: 'block' }} />
            </IconButton>
            {/* Date and time of appointment - shown when manuallySelectSlotTiming is true */}
            {manuallySelectSlotTiming && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(103, 121, 227, 0.2)' }}>
                <label htmlFor="date-time" style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Date and Time
                </label>
                <DateTimePicker
                  id="date-time"
                  value={formData?.startDate}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, startDate: e }))
                  }}
                  format={'DD-MM-YYYY hh:mm a'}
                />
              </Box>
            )}
          </Box>
          
          {/* Event Type Selector Buttons */}
          <EventSelectorForBooking
            clinicLocationId={selectedPatient?.clinicId}
            mentorProfileData={selectedMentorProfileData}
            eventTypesVisible={apptTypesToConsider?.map((apType) => ({
              ...apType,
              uid: apType?.id,
              enabled: true,
              durationInMins: apType?.duration,
              meetingSrc:
                apType?.mode === 'online' ? SESSION_PLATFORM_ONLINE : SESSION_PLATFORM_OFFLINE,
            }))}
            selectedEventTypeId={formData?.selectedAppointmentType}
            setSelectedEventTypeId={(e) => {
              const updatedFormData = { ...formData, selectedAppointmentType: e }
              setFormData(updatedFormData)
            }}
            isLoading={isLoading}
            useClinicServices
          />
          
          {/* Selected Session - Shown after event type is selected */}
          {selectedApptTypeObj && (
            <Box
              sx={{
                border: '2px solid #6779e3',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '20px',
                backgroundColor: 'rgba(103, 121, 227, 0.05)',
                boxShadow: '0 2px 8px rgba(103, 121, 227, 0.1)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 700 }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#6779e3' }}>Selected Session:</strong>{' '}
                  <span>{selectedApptTypeObj?.title} ({displayNamesOfEventTypes?.[selectedApptTypeObj?.type] || ''})</span>
                </div>
                {selectedMentorProfileData?.name && (
                  <div style={{ marginTop: '8px' }}>
                    <strong style={{ color: '#6779e3' }}>
                      {(() => {
                        if (Array.isArray(selectedMentorProfileData?.categories)) {
                          if (selectedMentorProfileData.categories.includes('therapist') || 
                              selectedMentorProfileData.categories.includes('clinical_psychologist') || 
                              selectedMentorProfileData.categories.includes('counselling_psychologist')) {
                            return 'Therapist:'
                          }
                          if (selectedMentorProfileData.categories.includes('doctor')) {
                            return 'Doctor:'
                          }
                        }
                        return 'Mentor:'
                      })()}
                    </strong>{' '}
                    <span>{selectedMentorProfileData?.name}</span>
                  </div>
                )}
              </div>
            </Box>
          )}
        </Grid>
        {!mentorProfileData && (
          <Grid xs={12} md={6} sx={{ p: 1 }}>
            <CustomSelect
              id="mentor"
              value={formData?.selectedMentor}
              label="Mentor"
              placeholder="Select Mentor"
              isLoading={isLoading}
              options={[
                {
                  id: 'un-assigned',
                  name: 'Un-Assigned',
                },
                ...mentorsList?.filter((mentor) => {
                  // if (selectedApptTypeObj?.type === EVENT_TYPE_THERAPY_SESSION) {
                  //   return mentor?.categories?.includes('therapist')
                  // }
                  // if (selectedApptTypeObj?.type === EVENT_TYPE_CONSULTATION) {
                  //   return mentor?.categories?.includes('doctor')
                  // }
                  // return false
                  return true
                }),
              ]}
              onChangeEvent={(e) => {
                setFormData({ ...formData, selectedMentor: e.target.value })
              }}
              selectType={selectTypes.string}
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => `${option?.name}`}
            />
          </Grid>
        )}
        {/* <Grid xs={12} md={6} sx={{ p: 1 }}>
          <CustomInput
            id="duration"
            value={formData?.duration}
            label="Duration in minutes"
            onChange={handleChange}
          />
        </Grid> */}
        {/* Date and time of appointment */}
        {manuallySelectSlotTiming && (
          <Grid xs={12} md={6} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
            <label htmlFor="date-time">Date and Time</label>
            <DateTimePicker
              id="date-time"
              value={formData?.startDate}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, startDate: e }))
              }}
              format={'DD-MM-YYYY hh:mm a'}
            />
          </Grid>
        )}
        {/* Meeting type */}
        {/* <Grid xs={12} md={6} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
          <CustomSelect
            id="type"
            value={formData?.meetingType}
            label="Meeting Type"
            placeholder="Select Meeting Type"
            options={BOOKED_EVENT_MODES}
            selectType={selectTypes.string}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.altTitle}
            onChangeEvent={handleChange}
          />
        </Grid> */}
        {/* skip billing checkbox */}
        <Grid xs={12} md={12} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipBilling}
                onChange={(e) => setSkipBilling(e.target.checked)}
                // sx={{ color: 'white' }}
              />
            }
            label="Skip Billing"
          />
        </Grid>
        {/* Fee input */}
        {!skipBilling && (
          <>
            <Grid xs={12} md={4} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
              <CustomSelect
                id="currency"
                value={formData?.currency}
                label="Currency"
                placeholder="Select Currency"
                isLoading={isLoading}
                options={currency_list}
                onChangeEvent={(e) => {
                  setFormData({ ...formData, currency: e.target.value })
                }}
                selectType={selectTypes.string}
                getOptionValue={(option) => option?.code}
                getOptionLabel={(option) => option?.name || option?.englishName || option?.symbol}
              />
            </Grid>
            <Grid xs={12} md={4} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
              <CustomInput
                id="fee"
                value={formData?.fee}
                label={`Fee (Default: ${getPriceStringWithCurrency(
                  selectedApptTypeObj?.pricingDetail?.price,
                  selectedApptTypeObj?.pricingDetail?.currency,
                )})`}
                type="number"
                onChange={handleChange}
              />
            </Grid>
            <Grid xs={12} md={4} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
              <>
                <CustomHTMLSelect
                  value={formData?.feePaymentMode}
                  onChange={(e) => {
                    setFormData({ ...formData, feePaymentMode: e.target.value })
                  }}
                  getOptionLabel={(option) => option?.title}
                  getOptionValue={(option) => option?.id}
                  options={OFFLINE_PAYMENT_METHODS_LIST?.filter((method) => {
                    if (method?.roleType === 'all') {
                      return true
                    }
                    if (
                      getAllRolesInBoolean().isL1TherapySupport ||
                      getAllRolesInBoolean().isL2TherapySupport ||
                      getAllRolesInBoolean().isL3TherapySupport
                    ) {
                      return method?.roleType === 'support'
                    }
                    if (getAllRolesInBoolean().isTeacher) {
                      if (selectedMentorProfileData?.categories?.includes('therapist')) {
                        return method?.roleType === 'therapist'
                      }
                      if (selectedMentorProfileData?.categories?.includes('doctor')) {
                        return method?.roleType === 'doctor'
                      }
                      return method?.roleType === 'mentor'
                    }
                    return false
                  })}
                  label="Payment Mode"
                />
              </>
            </Grid>
            {formData?.feePaymentMode !== 'cash' && (
              <Grid xs={12} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
                <CustomInput
                  value={formData?.feePaidTxnId}
                  label="Transaction ID"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      feePaidTxnId: e.target.value,
                    }))
                  }
                />
              </Grid>
            )}
            <Grid xs={12} sx={{ p: 1, flexDirection: 'column', display: 'flex', gap: 1 }}>
              <TextField
                multiline
                minRows={1}
                maxRows={4}
                fullWidth
                value={formData?.feeCollectionNote || ''}
                onChange={(e) => {
                  const sanitizedValue = e.target.value
                    .replace(/<[^>]*>/g, '') // Remove HTML tags
                    .replace(/[<>]/g, '') // Remove remaining angle brackets
                    .slice(0, 200) // Limit to 200 characters
                  setFormData((prev) => ({
                    ...prev,
                    feeCollectionNote: sanitizedValue,
                  }))
                }}
                inputProps={{
                  maxLength: 200,
                }}
                helperText={`${(formData?.feeCollectionNote || '').length}/200 characters`}
                variant="outlined"
                size="small"
                label="Note"
                placeholder="Add a note about this fee collection (optional)..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '14px',
                    '& textarea': {
                      resize: 'none',
                      overflow: 'hidden !important',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '12px',
                    marginTop: '4px',
                  },
                }}
              />
            </Grid>
          </>
        )}
        {selectedApptTypeObj?.type === EVENT_TYPE_CONSULTATION && (
          <Grid xs={12} md={12} sx={{ p: 1, display: 'flex', gap: 1 }}>
            <div className="d-flex flex-column w-100">
              <div className="d-flex align-items-center w-100">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tryBookingScreening}
                      onChange={(e) => {
                        setTryBookingScreening(e.target.checked)
                      }}
                      icon={<Cancel style={{ color: 'red' }} />}
                      checkedIcon={
                        screenerAvailable ? (
                          <CheckCircleIcon style={{ color: 'green' }} />
                        ) : (
                          <Error sx={{ color: '#ffaa09' }} />
                        )
                      }
                    />
                  }
                  color="secondary"
                  label="Book with Screening"
                />
                <AvailableScreenerComponent
                  eventBeingBooked={{
                    apiParam: 'booked-event-info',
                    mentorId: selectedMentorProfileData?.id,
                    userId: '',
                    startTime: dayjs?.utc(formData?.startDate)?.format('YYYY-MM-DD HH:mm:ss'),
                    endTime: dayjs
                      ?.utc(formData?.startDate)
                      .add(formData?.duration || selectedApptTypeObj?.duration, 'minutes')
                      ?.format('YYYY-MM-DD HH:mm:ss'),
                    eventType: selectedApptTypeObj?.type,
                    chamberRoomId: '',
                    eventTypeId: selectedApptTypeObj?.uid,
                    subscriptionPlanId: selectedApptTypeObj?.subscriptionPlanId,
                    patientId: selectedPatient?.id,
                    id: '',
                    feePaid: skipBilling ? 0 : formData?.fee,
                    currency: formData?.currency || 'inr',
                    feePaymentMode: 'paidOffline',
                    feePaidTxnId: '',
                    clinicId: selectedPatient?.clinicId || '',
                    clinicBrandId: selectedPatient?.clinicBrandId || '',
                  }}
                  setScreenerAvailable={setScreenerAvailable}
                />
              </div>
              {!tryBookingScreening && (
                <NewBookingDialogReasonForNotDoingScreeningForm
                  reasonForNotBooking={reasonForNotDoingScreening}
                  setReasonIdForNotDoingScreening={setReasonIdForNotDoingScreening}
                  setReasonForNotBooking={setReasonForNotDoingScreening}
                  screenerAvailable={screenerAvailable}
                  reasonForNotBookingScreeningId={reasonIdForNotDoingScreening}
                />
              )}
            </div>
          </Grid>
        )}
        <Grid xs={12} md={12} sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {creditsContext?.eligible && (
            <Button
              disabled={
                isBookingLoading ||
                creditsContextLoading ||
                !isNewBookingFormDataValid(formData, appointmentTypesList, mentorsList, skipBilling)
              }
              variant="outlined"
              color="success"
              onClick={handleCreditsBooking}
            >
              {creditsContextLoading ? 'Checking credits...' : 'Use Pack / Credits'}
            </Button>
          )}
          <Button
            disabled={
              isBookingLoading ||
              !isNewBookingFormDataValid(formData, appointmentTypesList, mentorsList, skipBilling)
            }
            variant="contained"
            onClick={handleOfflineBooking}
          >
            {isBookingLoading ? 'Booking...' : `Book ${bookButtonPostText}`}
          </Button>
          {formData?.feePaymentMode === 'aumhum' && (
            <HoldSlotAndSendLInkToPayButton
              selectedPatient={selectedPatient}
              paymentDetail={formData}
              bookingLoading={isBookingLoading}
              setBookingLoading={setIsBookingLoading}
              customFunctionForHoldSlotAndSendLinkToPay={handleHoldSlotAndBook}
            />
          )}
        </Grid>
        {creditsContext?.eligible && creditsContext?.summary && (
          <Grid xs={12} sx={{ px: 1, pb: 1 }}>
            <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{creditsContext.summary}</Box>
          </Grid>
        )}
      </Grid>
    </div>
  )
}
